/** Iter 639 — staff till split · PIN login incl. remembered store +
 * "Change store". Takes the till bag `{ t }` (login → t.setStaff). */
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, LockKeyhole } from "lucide-react";
import { tillApi, detail } from "@/features/staffTill/helpers";

export default function TillLogin({ t }) {
  // Iter-412 §7 — PIN-only after the first login: the store code + name
  // persist on the DEVICE (localStorage). A ?vendor= deep-link wins
  // over the saved store.
  const [params] = useSearchParams();
  const deepVendor = (params.get("vendor") || "").trim();
  const [saved] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("till_store") || "null");
    } catch {
      return null;
    }
  });
  const remembered = deepVendor
    ? { vendor_id: deepVendor, vendor_name: null }
    : saved?.vendor_id
    ? saved
    : null;
  const [changing, setChanging] = useState(false);
  const pinOnly = !!remembered && !changing;
  const [vendorId, setVendorId] = useState(remembered?.vendor_id || "");
  const [pin, setPin] = useState("");

  const loginMut = useMutation({
    mutationFn: async () =>
      (await tillApi.post("/classifieds/engagement/staff/login", { vendor_id: vendorId.trim(), pin })).data,
    onSuccess: (data) => {
      sessionStorage.setItem("till_token", data.access_token);
      sessionStorage.setItem("till_staff", JSON.stringify(data.staff || {}));
      // Device-persisted store for PIN-only logins next time.
      try {
        localStorage.setItem(
          "till_store",
          JSON.stringify({ vendor_id: vendorId.trim(), vendor_name: data.vendor_name || null }),
        );
      } catch { /* private mode */ }
      t.setStaff(data.staff || {});
    },
    onError: (err) => toast.error(detail(err, "Login failed — check the vendor ID and PIN.")),
  });

  return (
    <form
      className="mt-8 rounded-2xl bg-white shadow-card p-6 space-y-3"
      data-testid="till-login-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!vendorId.trim()) return toast.error("Enter your store's vendor ID.");
        if (!/^\d{4}$/.test(pin)) return toast.error("PIN is 4 digits.");
        loginMut.mutate();
      }}
    >
      <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
        <LockKeyhole className="w-4 h-4 text-slate-400" /> Staff sign-in
      </div>
      {pinOnly ? (
        <div className="flex items-center justify-between gap-2 rounded-full bg-slate-50 border border-slate-200 px-4 py-2.5" data-testid="till-remembered-store">
          <span className="text-sm font-bold text-slate-900 truncate">
            🏪 {remembered.vendor_name || remembered.vendor_id}
          </span>
          <button
            type="button"
            data-testid="till-change-store"
            onClick={() => {
              setChanging(true);
              setVendorId("");
            }}
            className="shrink-0 text-xs font-bold text-slate-500 hover:text-slate-800 underline"
          >
            Change store
          </button>
        </div>
      ) : (
        <input value={vendorId} onChange={(e) => setVendorId(e.target.value)} placeholder="Vendor ID (evd_…)"
          data-testid="till-vendor-input" className="w-full rounded-full border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:border-slate-500" />
      )}
      <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="4-digit PIN"
        inputMode="numeric" type="password" data-testid="till-pin-input"
        className="w-full rounded-full border border-slate-300 px-4 py-2.5 text-sm tracking-[0.4em] focus:outline-none focus:border-slate-500" />
      <button type="submit" disabled={loginMut.isPending} data-testid="till-login-submit"
        className="w-full rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold py-3 disabled:opacity-60 transition-colors">
        {loginMut.isPending ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Sign in"}
      </button>
    </form>
  );
}
